import { FunctionComponent, useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faPenToSquare, faDiagramSankey, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { type Automation } from '@spike/automation-model';
import {
    fetchAutomationsThunk,
    deleteAutomationThunk,
    setAutomationStatusThunk,
    AutomationsStatus
} from '@spike/automations-action';
import { useApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import { ConfirmDialog, Spinner } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import AutomationWizard from './Wizard/AutomationWizard';
import { NewAutomationDrawer, type CreationMode } from './NewAutomationDrawer';
import AutomationsTable from './List/AutomationsTable';
import AutomationAIPanel from './AI/AutomationAIPanel';
import AutomationTemplatesGallery from './Templates/AutomationTemplatesGallery';
import { type AutomationTemplate } from './Templates/automationTemplates';
import { automationFromTemplate } from './Templates/automationFromTemplate';
import dialogStyles from './automationDialog.module.scss';
import styles from './AutomationsComponent.module.scss';

const AutomationsComponent: FunctionComponent = () => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const { automations, status } = useSelector((state: RootState) => state.automations);

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showDrawer, setShowDrawer] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [showAIPanel, setShowAIPanel] = useState(false);
    const [showGallery, setShowGallery] = useState(false);
    const [automationName, setAutomationName] = useState<string | undefined>();
    const [editingAutomation, setEditingAutomation] = useState<Automation | undefined>();
    const [automationToDelete, setAutomationToDelete] = useState<Automation | undefined>();
    const [isDeleting, setIsDeleting] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const isLoading = status === AutomationsStatus.Initial || status === AutomationsStatus.Fetching;

    useEffect(() => {
        (dispatch as any)(fetchAutomationsThunk(apiClientWrapper));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleStartFromScratch = () => {
        setDropdownOpen(false);
        setShowDrawer(true);
    };

    const handleDrawerConfirm = (name: string, mode: CreationMode) => {
        setAutomationName(name);
        setShowDrawer(false);
        if (mode === 'ai') {
            setShowAIPanel(true);
        } else {
            setShowWizard(true);
        }
    };

    const handleWizardClose = useCallback(() => {
        setShowWizard(false);
        setEditingAutomation(undefined);
    }, []);

    const handleAIPanelClose = useCallback(() => {
        setShowAIPanel(false);
    }, []);

    const handleEditInBuilder = useCallback((automation: Automation) => {
        setShowAIPanel(false);
        setEditingAutomation(automation);
        setShowWizard(true);
    }, []);

    const handleEdit = useCallback((automation: Automation) => {
        setEditingAutomation(automation);
        setShowWizard(true);
    }, []);

    const handleDelete = useCallback((automation: Automation) => {
        setAutomationToDelete(automation);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!automationToDelete) return;

        setIsDeleting(true);
        try {
            await (dispatch as any)(deleteAutomationThunk(apiClientWrapper, automationToDelete.id));
            setAutomationToDelete(undefined);
        } finally {
            setIsDeleting(false);
        }
    }, [dispatch, apiClientWrapper, automationToDelete]);

    // Turning one off parks it in `paused`; `draft` is reserved for automations
    // that were never activated, so it must not be a state we can return to.
    const handleToggleStatus = useCallback(
        async (automation: Automation) => {
            const next = automation.status === 'active' ? 'paused' : 'active';

            await (dispatch as any)(setAutomationStatusThunk(apiClientWrapper, automation.id, next));
        },
        [dispatch, apiClientWrapper]
    );

    const handleStartWithTemplate = useCallback(() => {
        setDropdownOpen(false);
        setShowGallery(true);
    }, []);

    const handleTemplateSelected = useCallback((template: AutomationTemplate) => {
        setShowGallery(false);
        setEditingAutomation(automationFromTemplate(template));
        setShowWizard(true);
    }, []);

    // Both entry points ("Create New" in the empty state and in the header)
    // render the same menu. They previously duplicated this markup while
    // sharing one `dropdownRef`, so the click-outside handler only ever bound
    // to whichever happened to be mounted.
    const dropdownMenu = (
        <>
            <div
                className={styles.dropdownItem}
                onClick={handleStartWithTemplate}
            >
                <FontAwesomeIcon icon={faDiagramSankey} />
                Start With a Template
            </div>
            <div
                className={styles.dropdownItem}
                onClick={handleStartFromScratch}
            >
                <FontAwesomeIcon icon={faPenToSquare} />
                Start From Scratch
            </div>
        </>
    );

    const createDropdown = (
        <div
            className={styles.createButtonWrapper}
            ref={dropdownRef}
        >
            <button
                className={styles.createButton}
                onClick={() => setDropdownOpen(prev => !prev)}
            >
                Create New
                <FontAwesomeIcon icon={faChevronDown} />
            </button>

            {dropdownOpen && <div className={styles.dropdownMenu}>{dropdownMenu}</div>}
        </div>
    );

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            );
        }

        if (automations.length === 0) {
            return (
                <div className={styles.emptyContainer}>
                    <div className={styles.emptyIcon}>
                        <FontAwesomeIcon icon={faDiagramSankey} />
                    </div>
                    <h2 className={styles.emptyTitle}>
                        Create a New
                        <br />
                        Automation
                    </h2>
                    <p className={styles.emptyDescription}>Click the &quot;Create New&quot; button to add one.</p>
                    {createDropdown}
                </div>
            );
        }

        return (
            <div className={styles.tableContainer}>
                <AutomationsTable
                    automations={automations}
                    isLoading={status === AutomationsStatus.Fetching}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleStatus={handleToggleStatus}
                />
            </div>
        );
    };

    return (
        <>
            <div className={styles.root}>
                <div className={styles.headWrapper}>
                    <DefaultHeader
                        title="Automations"
                        textBelowTitle="View and manage all automated messages sent to your clients."
                        buttonLabel="Create New"
                        displayButton={automations.length > 0}
                        onClick={() => setDropdownOpen(prev => !prev)}
                        buttonIcon={faPlus}
                    />

                    {automations.length > 0 && dropdownOpen && (
                        <div
                            className={styles.headerDropdownMenu}
                            ref={dropdownRef}
                        >
                            {dropdownMenu}
                        </div>
                    )}
                </div>

                {renderContent()}
            </div>

            {showGallery && (
                <AutomationTemplatesGallery
                    onSelect={handleTemplateSelected}
                    onClose={() => setShowGallery(false)}
                />
            )}

            {showDrawer && (
                <NewAutomationDrawer
                    onClose={() => setShowDrawer(false)}
                    onConfirm={handleDrawerConfirm}
                />
            )}

            {showWizard &&
                createPortal(
                    <AutomationWizard
                        onClose={handleWizardClose}
                        initialName={automationName}
                        automation={editingAutomation}
                    />,
                    document.body
                )}

            {showAIPanel &&
                createPortal(
                    <AutomationAIPanel
                        onClose={handleAIPanelClose}
                        initialName={automationName}
                        onEditInBuilder={handleEditInBuilder}
                    />,
                    document.body
                )}

            <ConfirmDialog
                open={!!automationToDelete}
                className={dialogStyles.dialog}
                onCancel={isDeleting ? undefined : () => setAutomationToDelete(undefined)}
                title="Delete Automation?"
                question={
                    <>
                        {'"'}
                        {automationToDelete?.name}
                        {'" will be deleted permanently.'}
                    </>
                }
                subquestion="Clients already scheduled to receive it will stop receiving it. This cannot be undone."
                actions={
                    <>
                        <Button
                            fullWidth
                            size="large"
                            variant="danger"
                            label={isDeleting ? 'Deleting...' : 'Delete'}
                            disabled={isDeleting}
                            style={{ minWidth: 'unset' }}
                            onClick={handleConfirmDelete}
                        />
                        <Button
                            fullWidth
                            size="large"
                            variant="outlined"
                            label="Cancel"
                            disabled={isDeleting}
                            style={{ margin: 0, minWidth: 'unset' }}
                            onClick={() => setAutomationToDelete(undefined)}
                        />
                    </>
                }
            />
        </>
    );
};

export default AutomationsComponent;
